import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Euro, Calendar, Trash2, Upload, FileText, Download, Gift } from "lucide-react";
import { billingEntryTitle, creditReasonLabel, creditStatusLabel, isCreditEntry } from "@/lib/billingHistory";

const Zahlungen = () => {
  const [zahlungen, setZahlungen] = useState([]);
  const [abteilungen, setAbteilungen] = useState([]);
  const [wachen, setWachen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [zahlungToDelete, setZahlungToDelete] = useState(null);
  const [zahlungToUpload, setZahlungToUpload] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedWache, setSelectedWache] = useState("");
  const [formData, setFormData] = useState({
    abteilung_id: "",
    betrag: "",
    bezahlt_am: "",
    notiz: "",
  });

  // Helper to ensure we always have an array
  const toArray = (data) => Array.isArray(data) ? data : [];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [zahlRes, abtRes, wachenRes] = await Promise.all([
        axios.get(`${API}/zahlungen`),
        axios.get(`${API}/abteilungen`),
        axios.get(`${API}/wachen`),
      ]);
      setZahlungen(zahlRes.data);
      setAbteilungen(abtRes.data);
      setWachen(wachenRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/zahlungen`, {
        ...formData,
        betrag: parseFloat(formData.betrag),
      });
      toast.success("Zahlung erfolgreich vermerkt");
      setShowCreateDialog(false);
      setSelectedWache("");
      setFormData({
        abteilung_id: "",
        betrag: "",
        bezahlt_am: "",
        notiz: "",
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Vermerken");
    }
  };

  const handleDeleteClick = (zahlung) => {
    setZahlungToDelete(zahlung);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!zahlungToDelete) return;
    
    try {
      await axios.delete(`${API}/zahlungen/${zahlungToDelete.id}`);
      toast.success("Zahlung erfolgreich gelöscht");
      setShowDeleteDialog(false);
      setZahlungToDelete(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const handleUploadClick = (zahlung) => {
    setZahlungToUpload(zahlung);
    setSelectedFile(null);
    setShowUploadDialog(true);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error("Nur PDF-Dateien sind erlaubt");
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error("Datei ist zu groß (max. 10MB)");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUploadConfirm = async () => {
    if (!selectedFile || !zahlungToUpload) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      await axios.post(`${API}/zahlungen/${zahlungToUpload.id}/rechnung`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success("Rechnung erfolgreich hochgeladen");
      setShowUploadDialog(false);
      setZahlungToUpload(null);
      setSelectedFile(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Hochladen");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteRechnung = async (zahlungId, event) => {
    event.stopPropagation();
    
    if (!window.confirm("Rechnung wirklich löschen?")) return;
    
    try {
      await axios.delete(`${API}/zahlungen/${zahlungId}/rechnung`);
      toast.success("Rechnung gelöscht");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const handleDownloadRechnung = async (zahlungId, event) => {
    event.stopPropagation();
    
    try {
      const response = await axios.get(`${API}/zahlungen/${zahlungId}/rechnung`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Rechnung_${zahlungId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Handle blob response errors properly
      if (error.response && error.response.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const jsonError = JSON.parse(text);
          toast.error(jsonError.detail || "Fehler beim Herunterladen");
        } catch {
          toast.error("Fehler beim Herunterladen");
        }
      } else {
        toast.error(error.response?.data?.detail || "Fehler beim Herunterladen");
      }
    }
  };

  const getAbteilungInfo = (abteilungId) => {
    const abt = abteilungen.find((a) => a.id === abteilungId);
    if (!abt) return { name: "Unbekannt", wache: "Unbekannt" };
    
    const wache = wachen.find((w) => w.id === abt.wache_id);
    return {
      name: abt.name,
      wache: wache ? wache.name : "Unbekannt",
    };
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("de-DE");
  };

  const formatDateTime = (dateStr) => {
    return new Date(dateStr).toLocaleString("de-DE");
  };

  const filteredAbteilungen = selectedWache
    ? toArray(abteilungen).filter(a => a.wache_id === selectedWache && a.status === "active")
    : [];

  const handleWacheSelect = (wacheId) => {
    setSelectedWache(wacheId);
    setFormData({ ...formData, abteilung_id: "", betrag: "" });
  };

  const handleAbteilungSelect = (abteilungId) => {
    const abt = abteilungen.find(a => a.id === abteilungId);
    setFormData({
      ...formData,
      abteilung_id: abteilungId,
      betrag: abt ? (abt.billing_betrag ?? abt.betrag).toString() : ""
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-gray-600">Lädt...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="zahlungen-container">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Zahlungen</h1>
          <p className="text-gray-600">Zahlungen vermerken und Historie anzeigen</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="create-zahlung-button">
              <Plus className="w-4 h-4 mr-2" />
              Zahlung vermerken
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-gray-300">
            <DialogHeader>
              <DialogTitle className="text-gray-900">Zahlung vermerken</DialogTitle>
              <DialogDescription className="text-gray-600">
                Vermerken Sie eine eingegangene Zahlung
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate}>
              <div className="space-y-4 my-4">
                <div>
                  <Label htmlFor="wache_select" className="text-gray-700">1. Wache auswählen</Label>
                  <Select
                    value={selectedWache}
                    onValueChange={handleWacheSelect}
                    required
                  >
                    <SelectTrigger className="bg-white border-gray-300 text-gray-900" data-testid="zahlung-wache-select">
                      <SelectValue placeholder="Zuerst Wache auswählen" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-300">
                      {toArray(wachen).filter(w => w.status === "active").map((w) => (
                        <SelectItem key={w.id} value={w.id}>
                          {w.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedWache && (
                  <div>
                    <Label htmlFor="abteilung_id" className="text-gray-700">2. Abteilung auswählen</Label>
                    <Select
                      value={formData.abteilung_id}
                      onValueChange={handleAbteilungSelect}
                      required
                    >
                      <SelectTrigger className="bg-white border-gray-300 text-gray-900" data-testid="zahlung-abteilung-select">
                        <SelectValue placeholder="Abteilung auswählen" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-300">
                        {toArray(filteredAbteilungen).map((abt) => (
                          <SelectItem key={abt.id} value={abt.id}>
                            {abt.name} ({(abt.billing_betrag ?? abt.betrag ?? 0).toFixed(2)} €)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {formData.abteilung_id && (
                  <>
                    <div>
                      <Label htmlFor="betrag" className="text-gray-700">Betrag (€)</Label>
                      <Input
                        id="betrag"
                        data-testid="zahlung-betrag-input"
                        type="number"
                        step="0.01"
                        value={formData.betrag}
                        readOnly={abteilungen.some(a => a.id === formData.abteilung_id && a.billing_mode === "contract")}
                        onChange={(e) => setFormData({ ...formData, betrag: e.target.value })}
                        className="bg-white border-gray-300 text-gray-900"
                        placeholder="3.50"
                        required
                      />
                      {abteilungen.some(a => a.id === formData.abteilung_id && a.billing_mode === "contract") && (
                        <p className="mt-1 text-sm text-gray-600">Der Betrag ist durch den abgeschlossenen Vertrag festgelegt.</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="bezahlt_am" className="text-gray-700">Bezahlt am</Label>
                      <Input
                        id="bezahlt_am"
                        data-testid="zahlung-datum-input"
                        type="date"
                        value={formData.bezahlt_am}
                        onChange={(e) => setFormData({ ...formData, bezahlt_am: e.target.value })}
                        className="bg-white border-gray-300 text-gray-900"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="notiz" className="text-gray-700">Notiz (optional)</Label>
                      <Textarea
                        id="notiz"
                        data-testid="zahlung-notiz-input"
                        value={formData.notiz}
                        onChange={(e) => setFormData({ ...formData, notiz: e.target.value })}
                        className="bg-white border-gray-300 text-gray-900"
                        placeholder="z.B. Überweisung erhalten"
                        rows={3}
                      />
                    </div>
                  </>
                )}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setShowCreateDialog(false); setSelectedWache(""); }} className="border-gray-300 text-gray-700">
                  Abbrechen
                </Button>
                <Button type="submit" className="btn-primary" data-testid="zahlung-submit-button" disabled={!formData.abteilung_id}>
                  Vermerken
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Zahlungen List */}
      <div className="space-y-4">
        {zahlungen.length === 0 ? (
          <Card className="card-white">
            <CardContent className="py-12 text-center text-gray-600">
              Noch keine Zahlungen vermerkt. Vermerken Sie die erste Zahlung.
            </CardContent>
          </Card>
        ) : (
          toArray(zahlungen).map((zahlung) => {
            const info = getAbteilungInfo(zahlung.abteilung_id);
            if (isCreditEntry(zahlung)) {
              return (
                <Card key={zahlung.id} className="border-emerald-200 bg-emerald-50/40" data-testid={`credit-card-${zahlung.id}`}>
                  <CardHeader>
                    <div className="flex items-start justify-between gap-4">
                      <div><CardTitle className="text-gray-900 mb-1">{info.name}</CardTitle><p className="text-sm text-gray-600">{info.wache}</p></div>
                      <div className="flex items-center gap-2 text-right text-xl font-bold text-emerald-700"><Gift className="h-6 w-6" />{billingEntryTitle(zahlung)}</div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <div><p className="mb-1 text-sm text-gray-600">Grund</p><p className="font-medium text-gray-900">{creditReasonLabel(zahlung.reason)}</p></div>
                      <div><p className="mb-1 text-sm text-gray-600">Status</p><p className="font-medium text-gray-900">{creditStatusLabel(zahlung.status)}</p></div>
                      <div><p className="mb-1 text-sm text-gray-600">Erfasst am</p><p className="text-gray-900">{formatDateTime(zahlung.created_at)}</p></div>
                    </div>
                    {zahlung.status === "applied" && <div className="mt-4 grid grid-cols-1 gap-4 rounded-lg border border-emerald-200 bg-white p-3 md:grid-cols-2"><div><p className="mb-1 text-sm text-gray-600">Gratis-Zeitraum</p><p className="text-gray-900">{formatDate(zahlung.period_start)} bis {formatDate(zahlung.period_end)}</p></div><div><p className="mb-1 text-sm text-gray-600">Nächste Zahlung</p><p className="text-gray-900">{formatDate(zahlung.new_next_payment_due)}</p></div></div>}
                    {zahlung.status === "pending" && <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Vorgemerkt: wird nach der ersten gültigen Zahlung hinten angehängt.</p>}
                    {zahlung.status === "voided" && <p className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">Storniert{zahlung.void_reason ? `: ${zahlung.void_reason}` : ""}</p>}
                    {zahlung.note && <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3"><p className="mb-1 text-sm text-gray-600">Notiz:</p><p className="text-gray-800">{zahlung.note}</p></div>}
                    <div className="mt-4 border-t border-emerald-200 pt-4 text-sm text-gray-500">Vermerkt von: {zahlung.created_by || "Admin"}</div>
                  </CardContent>
                </Card>
              );
            }
            return (
              <Card key={zahlung.id} className="card-white card-hover" data-testid={`zahlung-card-${zahlung.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-gray-900 mb-1">{info.name}</CardTitle>
                      <p className="text-sm text-gray-600">{info.wache}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-green-600 flex items-center gap-2">
                        <Euro className="w-6 h-6" />
                        {(zahlung.betrag || 0).toFixed(2)} €
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Bezahlt am</p>
                      <p className="text-gray-900 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {formatDate(zahlung.bezahlt_am)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Fällig gewesen am</p>
                      <p className="text-gray-900">{formatDate(zahlung.faellig_gewesen_am)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Nächste Zahlung fällig</p>
                      <p className="text-gray-900">{formatDate(zahlung.naechste_zahlung_faellig_am)}</p>
                    </div>
                  </div>
                  {zahlung.notiz && (
                    <div className="mt-4 p-3 rounded-lg bg-gray-50 border border-gray-200">
                      <p className="text-sm text-gray-600 mb-1">Notiz:</p>
                      <p className="text-gray-800">{zahlung.notiz}</p>
                    </div>
                  )}
                  
                  {/* Rechnung Section */}
                  {zahlung.rechnung_pdf && (
                    <div className="mt-4 p-3 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-600" />
                        <span className="text-sm font-medium text-blue-900">Rechnung vorhanden</span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => handleDownloadRechnung(zahlung.id, e)}
                          className="border-blue-300 text-blue-600 hover:bg-blue-100"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => handleDeleteRechnung(zahlung.id, e)}
                          className="border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <div className="mt-4 pt-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="text-sm text-gray-500">
                      <div>Vermerkt von: {zahlung.vermerkt_von}</div>
                      <div>am {formatDateTime(zahlung.created_at)}</div>
                    </div>
                    <div className="flex gap-2">
                      {!zahlung.rechnung_pdf && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUploadClick(zahlung)}
                          className="border-blue-300 text-blue-600 hover:bg-blue-50"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Rechnung
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteClick(zahlung)}
                        className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                        data-testid={`delete-zahlung-${zahlung.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Löschen
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Zahlung löschen?</DialogTitle>
            <DialogDescription className="text-gray-600">
              Sind Sie sicher, dass Sie diese Zahlung löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
            </DialogDescription>
          </DialogHeader>
          {zahlungToDelete && (
            <div className="py-4">
              <div className="p-4 rounded-lg bg-gray-50 border border-gray-200 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Betrag:</span>
                  <span className="font-semibold text-gray-900">{(zahlungToDelete?.betrag || 0).toFixed(2)} €</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Bezahlt am:</span>
                  <span className="text-gray-900">{formatDate(zahlungToDelete.bezahlt_am)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Abteilung:</span>
                  <span className="text-gray-900">{getAbteilungInfo(zahlungToDelete.abteilung_id).name}</span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setZahlungToDelete(null);
              }}
              className="border-gray-300 text-gray-700"
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-zahlung"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Ja, löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Rechnung hochladen</DialogTitle>
            <DialogDescription className="text-gray-600">
              Laden Sie eine PDF-Rechnung für diese Zahlung hoch.
            </DialogDescription>
          </DialogHeader>
          {zahlungToUpload && (
            <div className="py-4">
              <div className="p-4 rounded-lg bg-gray-50 border border-gray-200 space-y-2 mb-4">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Betrag:</span>
                  <span className="font-semibold text-gray-900">{(zahlungToUpload?.betrag || 0).toFixed(2)} €</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Abteilung:</span>
                  <span className="text-gray-900">{getAbteilungInfo(zahlungToUpload.abteilung_id).name}</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="pdf-upload">PDF-Datei auswählen</Label>
                <Input
                  id="pdf-upload"
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="cursor-pointer"
                />
                {selectedFile && (
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <FileText className="w-4 h-4" />
                    <span>{selectedFile.name} ({(selectedFile.size / 1024).toFixed(0)} KB)</span>
                  </div>
                )}
                <p className="text-xs text-gray-500">Nur PDF-Dateien, max. 10MB</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowUploadDialog(false);
                setZahlungToUpload(null);
                setSelectedFile(null);
              }}
              className="border-gray-300 text-gray-700"
              disabled={uploading}
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              onClick={handleUploadConfirm}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={!selectedFile || uploading}
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? "Lädt hoch..." : "Hochladen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Zahlungen;
